import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRuns,
  getRun,
  getRunResults,
  retryRun,
  deleteRun,
  deleteRuns,
  retryRuns,
  RunFilters,
} from '../api/runs'
import { queryKeys } from '../lib/query-client'

/**
 * Hook to fetch list of runs.
 *
 * @param filters - Optional filters for status, workflow, pagination
 * @returns Query result with runs array, loading state, error
 */
export function useRuns(filters?: RunFilters) {
  return useQuery({
    queryKey: queryKeys.runs.list(filters as Record<string, unknown> || {}),
    queryFn: () => getRuns(filters),
  })
}

/**
 * Hook to fetch single run details.
 *
 * @param fileId - The run's file ID
 * @returns Query result with run detail, loading state, error
 */
export function useRun(fileId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.runs.detail(fileId || ''),
    queryFn: () => getRun(fileId!),
    enabled: !!fileId, // Only fetch when fileId is provided
  })
}

/**
 * Hook to fetch extraction results for a run.
 *
 * @param fileId - The run's file ID
 * @returns Query result with extraction results
 */
export function useRunResults(fileId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.runs.detail(fileId || ''), 'results'],
    queryFn: () => getRunResults(fileId!),
    enabled: !!fileId,
  })
}

/**
 * Hook to retry a failed run.
 *
 * @returns Mutation for retrying runs
 */
export function useRetryRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => retryRun(fileId),
    onSuccess: (_, fileId) => {
      // Invalidate the specific run
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.detail(fileId),
      })
      // Invalidate the runs list
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.lists(),
      })
    },
  })
}

/**
 * Prefetch a run's details for faster navigation.
 *
 * Call this on hover over a run row to preload data.
 */
export function usePrefetchRun() {
  const queryClient = useQueryClient()

  return (fileId: string) => {
    queryClient.prefetchQuery({
      queryKey: queryKeys.runs.detail(fileId),
      queryFn: () => getRun(fileId),
      staleTime: 1000 * 60 * 5, // 5 minutes
    })
  }
}

/**
 * Hook to delete a single run.
 */
export function useDeleteRun() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileId: string) => deleteRun(fileId),
    onSuccess: (_, fileId) => {
      // Invalidate the specific run
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.detail(fileId),
      })
      // Invalidate all runs lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.lists(),
      })
    },
  })
}

/**
 * Hook to delete multiple runs (bulk action).
 */
export function useBulkDeleteRuns() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileIds: string[]) => deleteRuns(fileIds),
    onSuccess: () => {
      // Invalidate all runs lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.lists(),
      })
    },
  })
}

/**
 * Hook to retry multiple runs (bulk action).
 */
export function useBulkRetryRuns() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (fileIds: string[]) => retryRuns(fileIds),
    onSuccess: () => {
      // Invalidate all runs lists
      queryClient.invalidateQueries({
        queryKey: queryKeys.runs.lists(),
      })
    },
  })
}
